import { usePaymentStatus } from '../hooks/useTallyQueries';
import { usePaymentStore, useUIStore } from '../store';
import { Button, LoadingSpinner } from './ui';
import { formatCurrency, formatDateTime } from '../utils/formatters';

interface PaymentSuccessProps {
  paymentRequestId: string | null;
}

export const PaymentSuccess = ({ paymentRequestId }: PaymentSuccessProps) => {
  const { resetPayment } = usePaymentStore();
  const { setActiveView } = useUIStore();
  
  const { data: paymentStatus, isLoading } = usePaymentStatus(paymentRequestId);

  const handleContinue = () => {
    resetPayment();
    setActiveView('dashboard');
  };

  const handleViewHistory = () => {
    resetPayment();
    setActiveView('history');
  };

  if (isLoading || !paymentStatus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-sm w-full">
          <LoadingSpinner size="large" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Confirming Payment</h2>
          <p className="text-gray-600 text-sm">
            Please wait while we confirm your payment...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
        {/* Success Icon */}
        <div className="text-center p-6 border-b">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-xl font-semibold text-green-600 mb-2">
            Payment Successful!
          </h2>
          <p className="text-gray-600">
            Your payment has been processed successfully.
          </p>
        </div>

        {/* Payment Details */}
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1">Amount Paid</p>
                <p className="font-semibold text-lg text-green-600">
                  {formatCurrency(paymentStatus.amount)}
                </p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Status</p>
                <p className="font-semibold text-green-600">
                  {paymentStatus.status}
                </p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Transaction ID</p>
                <p className="font-mono text-xs">
                  {paymentStatus.transactionId}
                </p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Processed At</p>
                <p className="text-xs">
                  {paymentStatus.processedAt && formatDateTime(paymentStatus.processedAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Voucher Status */}
          {paymentStatus.voucherStatus && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">📄</span>
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Tally Voucher Status
                  </p>
                  <p className="text-xs text-blue-700">
                    {paymentStatus.voucherStatus === 'QUEUED' && 'Voucher creation is in progress'}
                    {paymentStatus.voucherStatus === 'SYNCED' && 'Voucher has been created in Tally'}
                    {paymentStatus.voucherStatus === 'FAILED' && 'Voucher creation failed - will retry automatically'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t space-y-3">
          <Button
            onClick={handleContinue}
            variant="primary"
            size="large"
            className="w-full"
          >
            Back to Dashboard
          </Button>
          
          <Button
            onClick={handleViewHistory}
            variant="outline"
            size="medium"
            className="w-full"
          >
            View Payment History
          </Button>
        </div>

        {/* Updated Balance */}
        {/* <div className="px-6 pb-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Updated Balance</h3>
          <EffectiveBalanceDisplay showDetails={true} />
        </div> */}

        {/* Important Note */}
        <div className="px-6 pb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Balance Update:</strong> Your effective balance has been updated immediately. 
              Tally sync will occur within 2-3 minutes, and the voucher will be created within 15-20 minutes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};